import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown, ChevronRight, X, UserPlus, ListChecks,
} from "lucide-react";
import { ContactPicker } from "@/components/ContactPicker";
import { useOpenContactPanel } from "@/components/sidepanel/registry";
import { useAuth } from "@/hooks/useAuth";
import type {
  AccordionPayload, GewerkStatus, GewerkUpdatePatch, GewerkContactLink, Contact,
} from "@/types";

const gewerkStatusLabels: Record<GewerkStatus, string> = {
  zu_erledigen: 'Zu erledigen',
  in_arbeit: 'In Arbeit',
  fertig: 'Fertig',
};

const gewerkStatusClasses: Record<GewerkStatus, string> = {
  zu_erledigen: 'bg-gray-100 text-gray-800',
  in_arbeit: 'bg-amber-100 text-amber-800',
  fertig: 'bg-green-100 text-green-800',
};

const gewerkStatusOrder: GewerkStatus[] = ['zu_erledigen', 'in_arbeit', 'fertig'];

// Prozess-Daten (Bautenstandsliste/Prozessliste): reine DATE-Felder,
// Labels in Bauablaufplan-Sprache.
const gewerkDateFields = [
  { key: 'angefragt_am', label: 'Angefragt' },
  { key: 'beauftragt_am', label: 'Beauftragt' },
  { key: 'fertiggestellt_am', label: 'Fertiggestellt' },
] as const;

type GewerkDateField = (typeof gewerkDateFields)[number]['key'];

/** Heutiges Datum als YYYY-MM-DD in Europe/Berlin (Wanduhr der App-Daten). */
function todayBerlinISO(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

/** YYYY-MM-DD → DD.MM.YYYY (reines Datum, keine Zeitzonen-Mathe nötig). */
function formatDateDE(iso: string | null): string {
  return iso ? iso.split('-').reverse().join('.') : '';
}

function contactName(contact: GewerkContactLink): string {
  return (
    contact.display_name ||
    [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
    contact.company_name ||
    contact.email_address ||
    'Unbenannter Kontakt'
  );
}

interface GewerkAccordionProps {
  payload: AccordionPayload;
  /** Kontakte am Gewerk pflegen: approver+ (Einkaufsbeziehung). */
  isApprover: boolean;
  /** Status + Prozess-Daten pflegen: Genehmiger ODER Bauleiter dieses BVs
   *  (Bautenstandsliste — der Bauleiter trägt DATEN ein, nie „erledigt"). */
  canEditDates: boolean;
  onUpdate: (gewerkId: number, patch: GewerkUpdatePatch) => Promise<void>;
  onAddContact: (gewerkId: number, contact: Contact) => Promise<void>;
  onRemoveContact: (gewerkId: number, linkId: number) => Promise<void>;
}

/**
 * Bauablauf-Checkliste: der gesamte aktive Gewerk-Katalog, gruppiert
 * nach Phase als Akkordeon. Status und Prozess-Daten (Angefragt/
 * Beauftragt/Fertiggestellt als Datum) pflegen Genehmiger/Admin und der
 * Bauleiter des Bauvorhabens; Kontakte nur Genehmiger. Alle anderen
 * sehen Status-Badge, Daten und Dauer lesend.
 */
export function GewerkAccordion({
  payload,
  isApprover,
  canEditDates,
  onUpdate,
  onAddContact,
  onRemoveContact,
}: GewerkAccordionProps) {
  const openContactInPanel = useOpenContactPanel();
  // Ohne Kontakte-Modul (z. B. Vertrieb) werden Kontakt-Chips als Text
  // gerendert — ein Link würde auf die gesperrte Kontakt-Seite führen.
  const { hasModule } = useAuth();
  const canKontakte = hasModule('kontakte');
  // Kontakt-Chips öffnen die Split-View-Seitenleiste statt zu navigieren;
  // Modifikator-Klick / mittlere Maustaste bleibt natives Browser-Verhalten.
  const openContactFromLink = (e: React.MouseEvent, contactId: number, name?: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    openContactInPanel(contactId, name);
  };
  // Erste Phase expandiert, Rest kollabiert (Lese-Reihenfolge des Ablaufs).
  const [expandedPhaseIds, setExpandedPhaseIds] = useState<Set<number>>(
    () => new Set(payload.phases.length > 0 ? [payload.phases[0].id] : [])
  );
  // EIN geteilter Picker — rendert unter der Zeile, für die er geöffnet wurde.
  const [pickerGewerkId, setPickerGewerkId] = useState<number | null>(null);

  const togglePhase = (phaseId: number) => {
    setExpandedPhaseIds((prev) => {
      const next = new Set(prev);
      if (next.has(phaseId)) {
        next.delete(phaseId);
      } else {
        next.add(phaseId);
      }
      return next;
    });
  };

  // Status-Pille: „Fertig" setzt immer ein Fertigstellungsdatum (Default
  // heute) — die API-Invariante fertig⇔Datum wird so nie sichtbar gebrochen.
  const handleStatus = (
    gewerk: { id: number; status: GewerkStatus; fertiggestellt_am: string | null },
    status: GewerkStatus,
  ) => {
    const patch: GewerkUpdatePatch = { status };
    if (status === 'fertig' && !gewerk.fertiggestellt_am) {
      patch.fertiggestellt_am = todayBerlinISO();
    }
    if (status !== 'fertig' && gewerk.fertiggestellt_am) {
      patch.fertiggestellt_am = null;
    }
    onUpdate(gewerk.id, patch);
  };

  // Datum eintragen: Fertigstellungsdatum ⇔ Status fertig atomar mitsenden.
  const handleDate = (
    gewerk: { id: number; status: GewerkStatus },
    field: GewerkDateField,
    value: string,
  ) => {
    const patch: GewerkUpdatePatch = { [field]: value || null };
    if (field === 'fertiggestellt_am') {
      if (value) patch.status = 'fertig';
      else if (gewerk.status === 'fertig') patch.status = 'in_arbeit';
    }
    onUpdate(gewerk.id, patch);
  };

  return (
    <div className="space-y-3">
      {payload.phases.map((phase) => {
        const expanded = expandedPhaseIds.has(phase.id);
        const doneCount = phase.gewerke.filter((g) => g.status === 'fertig').length;
        return (
          <div key={phase.id} className="rounded-lg border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => togglePhase(phase.id)}
              className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-gray-50"
              aria-expanded={expanded}
            >
              {expanded
                ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
              <span className="flex-1 text-sm font-semibold text-gray-900">{phase.name}</span>
              <span className="text-xs text-gray-500">
                {doneCount} fertig / {phase.gewerke.length} Gewerke
              </span>
            </button>
            {expanded && (
              phase.gewerke.length === 0 ? (
                <p className="px-4 pb-3 text-sm text-gray-500">Keine aktiven Gewerke in dieser Phase.</p>
              ) : (
                <ul className="divide-y divide-gray-100 border-t border-gray-100">
                  {phase.gewerke.map((gewerk) => (
                    <li key={gewerk.id} className="px-4 py-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm text-gray-900">{gewerk.name}</span>
                        {canEditDates ? (
                          <div className="flex gap-1">
                            {gewerkStatusOrder.map((status) => (
                              <button
                                key={status}
                                type="button"
                                onClick={() => handleStatus(gewerk, status)}
                                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                                  gewerk.status === status
                                    ? gewerkStatusClasses[status]
                                    : 'border border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                }`}
                              >
                                {gewerkStatusLabels[status]}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${gewerkStatusClasses[gewerk.status]}`}>
                            {gewerkStatusLabels[gewerk.status]}
                          </span>
                        )}
                      </div>
                      {/* Prozess-Daten (Bautenstandsliste): Daten statt „erledigt". */}
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
                        {gewerkDateFields.map(({ key, label }) => (
                          <label key={key} className="inline-flex items-center gap-1.5">
                            <span className="text-gray-400">{label}</span>
                            {canEditDates ? (
                              <input
                                type="date"
                                value={gewerk[key] ?? ''}
                                onChange={(e) => handleDate(gewerk, key, e.target.value)}
                                className="rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-800 focus:border-primary-500 focus:outline-none"
                              />
                            ) : (
                              <span className={gewerk[key] ? 'text-gray-800' : 'text-gray-300'}>
                                {formatDateDE(gewerk[key]) || '—'}
                              </span>
                            )}
                          </label>
                        ))}
                        {gewerk.dauer_tage != null && (
                          <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] text-green-800">
                            Dauer: {gewerk.dauer_tage} {gewerk.dauer_tage === 1 ? 'Tag' : 'Tage'}
                          </span>
                        )}
                        {gewerk.tage_seit_beauftragt != null && gewerk.tage_seit_beauftragt >= 0 && (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">
                            seit {gewerk.tage_seit_beauftragt} {gewerk.tage_seit_beauftragt === 1 ? 'Tag' : 'Tage'} beauftragt
                          </span>
                        )}
                      </div>
                      {canEditDates && (
                        <input
                          type="text"
                          defaultValue={gewerk.note ?? ''}
                          placeholder="Bemerkung…"
                          onBlur={(e) => {
                            const value = e.target.value.trim() || null;
                            if (value !== (gewerk.note ?? null)) {
                              onUpdate(gewerk.id, { note: value });
                            }
          }}
                          className="mt-2 w-full rounded border border-gray-200 px-2 py-1 text-xs text-gray-800 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none"
                        />
                      )}
                      {(gewerk.contacts.length > 0 || isApprover) && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {gewerk.contacts.map((contact) => (
                            <span
                              key={contact.link_id}
                              className="inline-flex items-center gap-1 rounded-full bg-gray-100 pl-2.5 pr-1 py-0.5 text-xs text-gray-800"
                            >
                            {canKontakte ? (
                              <Link
                                to={`/contacts/${contact.contact_id}`}
                                onClick={(e) => openContactFromLink(e, contact.contact_id, contactName(contact))}
                                className="hover:text-primary-700"
                              >
                                {contactName(contact)}
                              </Link>
                            ) : (
                              <span>{contactName(contact)}</span>
                            )}
                              {isApprover && (
                                <button
                                  type="button"
                                  onClick={() => onRemoveContact(gewerk.id, contact.link_id)}
                                  className="rounded-full p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                  title="Zuordnung entfernen"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          ))}
                          {isApprover && (
                            <button
                              type="button"
                              onClick={() => setPickerGewerkId(pickerGewerkId === gewerk.id ? null : gewerk.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs text-gray-500 hover:border-primary-300 hover:text-primary-700"
                              title="Kontakt zuordnen"
                            >
                              <UserPlus className="h-3 w-3" />
                              Kontakt
                            </button>
                          )}
                        </div>
                      )}
                      {pickerGewerkId === gewerk.id && (
                        <div className="mt-2">
                          <ContactPicker
                            placeholder="Kontakt für dieses Gewerk suchen…"
                            onSelect={(contact) => {
                              const pickedGewerkId = gewerk.id;
                              setPickerGewerkId(null);
                              onAddContact(pickedGewerkId, contact);
                            }}
                          />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )
            )}
          </div>
        );
      })}
      {payload.phases.length === 0 && (
        <p className="text-sm text-gray-500">
          <ListChecks className="mr-1 inline h-4 w-4 text-gray-400" />
          Der Gewerk-Katalog ist noch leer.
        </p>
      )}
    </div>
  );
}